<template>
  <!-- Card Normal de Encerramento (substituindo o dialog) -->
  <v-card
    variant="outlined"
    class="encerramento-card"
  >
    <v-toolbar
      color="primary"
      density="compact"
    >
      <v-toolbar-title class="text-white">
        Encerrar Atendimento
      </v-toolbar-title>
      
      <v-btn
        icon
        variant="text"
        @click="fechar"
      >
        <v-icon color="white">
          close
        </v-icon>
      </v-btn>
    </v-toolbar>

    <v-card-text class="pa-4">
      <!-- Loading State -->
      <v-alert
        v-if="carregandoProntuario || carregandoLaudo"
        type="info"
        variant="tonal"
        class="mb-4"
        border="start"
        density="compact"
      >
        <div class="d-flex align-center">
          <v-progress-circular
            indeterminate
            size="20"
            width="2"
            class="mr-3"
          />
          Carregando informações do prontuário e laudos...
        </div>
      </v-alert>

      <!-- Resumo do Atendimento -->
      <v-card
        variant="outlined"
        class="mb-4 resumo-card"
      >
        <v-card-title class="text-subtitle-1 font-weight-medium d-flex align-center">
          <v-icon
            color="primary"
            class="mr-2"
          >
            info
          </v-icon>
          Resumo do Atendimento
        </v-card-title>
        <v-divider class="mx-4" />
        <v-card-text>
          <v-row dense>
            <v-col
              cols="12"
              sm="6"
              md="3"
            >
              <div class="text-caption text-medium-emphasis mb-1">
                Paciente
              </div>
              <div class="text-body-1 font-weight-medium text-primary">
                {{ mFicha.nomePaciente }}
              </div>
            </v-col>
            <v-col
              cols="12"
              sm="6"
              md="3"
            >
              <div class="text-caption text-medium-emphasis mb-1">
                Ordem de Serviço
              </div>
              <div class="text-body-1 font-weight-medium text-primary">
                {{ mFicha.idOrdemServico }}
              </div>
            </v-col>
            <v-col
              cols="12"
              sm="6"
              md="3"
            >
              <div class="text-caption text-medium-emphasis mb-1">
                Tempo de Atendimento
              </div>
              <div class="text-body-1 font-weight-medium text-primary">
                {{ tempoAtd }}
              </div>
            </v-col>
            <v-col
              cols="12"
              sm="6"
              md="3"
            >
              <div class="text-caption text-medium-emphasis mb-1">
                Médico
              </div>
              <div class="text-body-1 font-weight-medium text-primary">
                {{ user.nome }}
              </div>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- Status do Prontuário e Laudos -->
      <v-card
        variant="outlined"
        class="mb-4 status-card"
      >
        <v-card-title class="text-subtitle-1 font-weight-medium d-flex align-center">
          <v-icon
            color="primary"
            class="mr-2"
          >
            assignment
          </v-icon>
          Status dos Documentos
        </v-card-title>
        <v-divider class="mx-4" />
        <v-card-text>
          <v-row dense>
            <v-col
              cols="12"
              md="6"
            >
              <v-card
                variant="flat"
                class="pa-3"
                :color="temProntuarioValido ? 'success-lighten-5' : 'warning-lighten-5'"
              >
                <div class="d-flex align-center">
                  <v-icon
                    :color="temProntuarioValido ? 'success' : 'warning'"
                    class="mr-2"
                  >
                    {{ temProntuarioValido ? 'check_circle' : 'warning' }}
                  </v-icon>
                  <div>
                    <div class="text-body-2 font-weight-medium">
                      Prontuário
                    </div>
                    <div class="text-caption">
                      {{ temProntuarioValido ? 'Preenchido' : 'Pendente' }}
                      <span
                        v-if="carregandoProntuario"
                        class="text-caption text-medium-emphasis"
                      >(carregando...)</span>
                    </div>
                  </div>
                </div>
              </v-card>
            </v-col>
            <v-col
              cols="12"
              md="6"
            >
              <v-card
                variant="flat"
                class="pa-3"
                :color="temLaudosPreenchidos ? 'success-lighten-5' : 'warning-lighten-5'"
              >
                <div class="d-flex align-center">
                  <v-icon
                    :color="temLaudosPreenchidos ? 'success' : 'warning'"
                    class="mr-2"
                  >
                    {{ temLaudosPreenchidos ? 'check_circle' : 'warning' }}
                  </v-icon>
                  <div>
                    <div class="text-body-2 font-weight-medium">
                      Laudos
                    </div>
                    <div class="text-caption">
                      {{ laudosPreenchidos }}/{{ examesComLaudoObrigatorio }} preenchidos
                      <span
                        v-if="carregandoLaudo"
                        class="text-caption text-medium-emphasis"
                      >(carregando...)</span>
                    </div>
                  </div>
                </div>
              </v-card>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- Seleção de Exames Realizados -->
      <v-card
        variant="outlined"
        class="mb-4 exames-card"
      >
        <v-card-title class="text-subtitle-1 font-weight-medium d-flex align-center">
          <v-icon
            color="primary"
            class="mr-2"
          >
            medical_services
          </v-icon>
          Exames Realizados
        </v-card-title>
        <v-divider class="mx-4" />
        <v-card-text>
          <div class="text-caption text-medium-emphasis mb-3">
            Selecione os exames que foram realizados neste atendimento:
          </div>
          
          <v-list
            class="exames-list"
            lines="two"
          >
            <v-list-item
              v-for="(consulta, index) in consultasDisponiveis"
              :key="index"
              :disabled="!consulta.disponivel"
              :class="{
                'exame-selecionado': consulta.selecionado,
                'exame-obrigatorio': consulta.exigeLaudoClinico || consulta.exigeProntuario,
                'exame-invalido': !consulta.disponivel,
                'exame-item': true
              }"
              variant="outlined"
              rounded="lg"
              class="mb-2"
              @click="toggleExame(consulta)"
            >
              <template #prepend>
                <v-checkbox
                  :model-value="consulta.selecionado"
                  hide-details
                  class="mt-0"
                  :color="getCorExame(consulta)"
                  :disabled="!consulta.disponivel"
                  @click.stop="toggleExame(consulta)"
                />
              </template>
              
              <v-list-item-title
                class="text-body-2 d-flex align-center flex-wrap"
                :class="consulta.colorText"
              >
                {{ consulta.nomeExame }}
                <v-chip
                  v-if="consulta.status === 'A'"
                  size="x-small"
                  color="warning"
                  variant="outlined"
                  class="ml-2"
                >
                  Aberto
                </v-chip>
                <v-chip
                  v-if="!consulta.disponivel"
                  size="x-small"
                  color="grey"
                  variant="outlined"
                  class="ml-2"
                >
                  Não Disponível
                </v-chip>
                <v-chip
                  v-if="consulta.debugInfo"
                  size="x-small"
                  color="info"
                  variant="outlined"
                  class="ml-2"
                >
                  {{ consulta.debugInfo }}
                </v-chip>
              </v-list-item-title>
              
              <v-list-item-subtitle
                class="text-caption mt-1"
                :class="consulta.colorText"
              >
                {{ consulta.nomeEspecialidade }}
                <v-chip
                  v-if="consulta.prioridade"
                  size="x-small"
                  color="orange"
                  variant="outlined"
                  class="ml-2"
                >
                  Prioridade
                </v-chip>
              </v-list-item-subtitle>

              <template #append>
                <div class="d-flex flex-column align-end gap-1">
                  <v-chip
                    v-if="consulta.exigeLaudoClinico"
                    size="x-small"
                    :color="consulta.laudoPreenchido ? 'success' : 'warning'"
                    variant="outlined"
                    class="status-chip"
                  >
                    <v-icon
                      :icon="consulta.laudoPreenchido ? 'check_circle' : 'warning'"
                      size="x-small"
                      class="mr-1"
                    />
                    {{ consulta.laudoPreenchido ? 'Laudo OK' : 'Laudo Pendente' }}
                  </v-chip>
                  
                  <v-chip
                    v-if="consulta.exigeProntuario"
                    size="x-small"
                    :color="temProntuarioValido ? 'success' : 'warning'"
                    variant="outlined"
                    class="status-chip"
                  >
                    <v-icon
                      :icon="temProntuarioValido ? 'check_circle' : 'warning'"
                      size="x-small"
                      class="mr-1"
                    />
                    {{ temProntuarioValido ? 'Prontuário OK' : 'Prontuário Pendente' }}
                  </v-chip>

                  <v-chip
                    v-if="consulta.exigeFichaAso"
                    size="x-small"
                    :color="consulta.statusFormulario?.existeAlgumFormulario ? 'success' : 'warning'"
                    variant="outlined"
                    class="status-chip"
                  >
                    <v-icon
                      :icon="consulta.statusFormulario?.existeAlgumFormulario ? 'check_circle' : 'warning'"
                      size="x-small"
                      class="mr-1"
                    />
                    {{ consulta.statusFormulario?.existeAlgumFormulario ? 'Ficha OK' : 'Ficha Pendente' }}
                  </v-chip>
                </div>
              </template>
            </v-list-item>
          </v-list>

          <!-- Alertas de Verificação -->
          <v-alert
            v-if="alertas.length > 0"
            type="warning"
            variant="tonal"
            class="mt-4"
            border="start"
            density="compact"
          >
            <template #title>
              <v-icon
                color="warning"
                class="mr-2"
              >
                warning
              </v-icon>
              Verificações Pendentes
            </template>
            <ul class="mb-0 ps-3">
              <li
                v-for="(alerta, index) in alertas"
                :key="index"
                class="text-caption"
              >
                {{ alerta }}
              </li>
            </ul>
          </v-alert>

          <!-- Mensagem quando não há exames selecionados -->
          <v-alert
            v-if="examesSelecionados.length === 0 && consultasDisponiveis.length > 0"
            type="info"
            variant="tonal"
            class="mt-4"
            border="start"
            density="compact"
          >
            <v-icon
              color="info"
              class="mr-2"
            >
              info
            </v-icon>
            Selecione pelo menos um exame para encerrar o atendimento.
          </v-alert>

          <!-- Mensagem quando não há exames disponíveis -->
          <v-alert
            v-if="consultasDisponiveis.length === 0"
            type="info"
            variant="tonal"
            class="mt-4"
            border="start"
            density="compact"
          >
            <v-icon
              color="info"
              class="mr-2"
            >
              info
            </v-icon>
            Não há exames disponíveis para encerramento.
          </v-alert>
        </v-card-text>
      </v-card>
    </v-card-text>

    <v-card-actions class="pa-4">
      <v-spacer />
      <v-btn
        color="grey"
        variant="outlined"
        :disabled="carregando"
        class="action-btn"
        @click="fechar"
      >
        <v-icon
          icon="close"
          start
        />
        Cancelar
      </v-btn>
      <v-btn
        color="primary"
        variant="flat"
        :loading="carregando"
        :disabled="!podeEncerrar || carregando || carregandoProntuario || carregandoLaudo || temAlertasBloqueantes"
        class="action-btn"
        @click="verificarExamesComStatusAberto"
      >
        <v-icon
          icon="check_circle"
          start
        />
        Verificar e Encerrar
      </v-btn>
    </v-card-actions>
  </v-card>

  <!-- Diálogos de Alerta (mantidos como diálogos) -->
  <v-dialog
    v-model="dialogAlerta"
    max-width="500"
  >
    <v-card>
      <v-toolbar
        color="warning"
        density="compact"
      >
        <v-icon
          icon="warning"
          start
          color="white"
        />
        <v-toolbar-title class="text-white">
          Atenção
        </v-toolbar-title>
        <v-spacer />
        <v-btn
          icon
          variant="text"
          @click="dialogAlerta = false"
        >
          <v-icon color="white">
            close
          </v-icon>
        </v-btn>
      </v-toolbar>
      <v-card-text class="pt-4 text-body-1">
        {{ mensagemAlerta }}
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn
          color="primary"
          variant="flat"
          @click="dialogAlerta = false"
        >
          Entendi
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <v-dialog
    v-model="dialogExameComplementar"
    persistent
    max-width="400"
  >
    <v-card>
      <v-card-title class="text-h6 d-flex align-center">
        <v-icon
          color="info"
          class="mr-2"
        >
          info
        </v-icon>
        Exame Complementar
      </v-card-title>
      <v-card-text class="text-body-2">
        Não há exame complementar solicitado. Deseja encerrar o atendimento? 
        <strong class="text-warning">Ao encerrar, não será possível imprimir itens deste atendimento.</strong>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn
          color="grey"
          variant="text"
          @click="dialogExameComplementar = false"
        >
          Não
        </v-btn>
        <v-btn
          color="primary"
          variant="flat"
          @click="confirmarEncerramentoForcado"
        >
          Sim
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <v-dialog
    v-model="erroProntuarioVazio"
    persistent
    max-width="400"
  >
    <v-card>
      <v-card-title class="text-h6 d-flex align-center">
        <v-icon
          color="error"
          class="mr-2"
        >
          error
        </v-icon>
        Prontuário Obrigatório
      </v-card-title>
      <v-card-text class="text-body-2">
        É necessário ter um prontuário cadastrado neste atendimento para prosseguir com o encerramento.
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn
          color="primary"
          variant="flat"
          @click="erroProntuarioVazio = false"
        >
          Ok
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <v-dialog
    v-model="dialogLaudo"
    persistent
    max-width="450"
  >
    <v-card>
      <v-card-title class="text-h6 d-flex align-center">
        <v-icon
          color="warning"
          class="mr-2"
        >
          warning
        </v-icon>
        Laudos Pendentes
      </v-card-title>
      <v-card-text class="text-body-2">
        Existem exames em aberto que exigem um laudo clínico.
        <strong class="text-warning">Certifique-se de preenchê-los antes de continuar.</strong>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn
          color="primary"
          variant="flat"
          @click="dialogLaudo = false"
        >
          Voltar para Preencher
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <v-dialog
    v-model="dialogProntuario"
    persistent
    max-width="450"
  >
    <v-card>
      <v-card-title class="text-h6 d-flex align-center">
        <v-icon
          color="warning"
          class="mr-2"
        >
          warning
        </v-icon>
        Prontuário Pendente
      </v-card-title>
      <v-card-text class="text-body-2">
        Alguns exames requerem o preenchimento do prontuário. 
        <strong class="text-warning">Certifique-se de atualizá-lo antes de prosseguir.</strong>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn
          color="primary"
          variant="flat"
          @click="dialogProntuario = false"
        >
          Voltar para Preencher
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <v-dialog
    v-model="desejaEncerrar"
    persistent
    max-width="450"
  >
    <v-card>
      <v-card-title class="text-h6 d-flex align-center">
        <v-icon
          color="primary"
          class="mr-2"
        >
          check_circle
        </v-icon>
        Confirmar Encerramento
      </v-card-title>
      <v-card-text class="text-body-2">
        <strong class="text-primary">Confirme para encerrar o atendimento!</strong>
        <div class="mt-2 text-medium-emphasis">
          Esta ação não poderá ser desfeita.
        </div>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn
          color="grey"
          variant="text"
          @click="desejaEncerrar = false"
        >
          Cancelar
        </v-btn>
        <v-btn
          color="primary"
          variant="flat"
          @click="confirmarEncerramento"
        >
          Confirmar Encerramento
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { ref, computed, watch, onMounted } from 'vue'
import medicoService from '../../../services/medico.service'
import posAtendimentoService from '../../../services/posAtendimento.service'

export default {
  name: 'EncerramentoAtendimento',
  
  props: {
    mFicha: {
      type: Object,
      required: true
    },
    consultasPaciente: {
      type: Array,
      default: () => []
    },
    user: {
      type: Object,
      required: true
    },
    tempoAtd: {
      type: String,
      default: '00:00:00'
    }
  },
  
  emits: ['atendimento-encerrado', 'fechar', 'prontuario-carregado', 'laudo-carregado'],
  
  setup(props, { emit }) {
    // Refs para diálogos
    const dialogAlerta = ref(false)
    const dialogExameComplementar = ref(false)
    const erroProntuarioVazio = ref(false)
    const dialogLaudo = ref(false)
    const dialogProntuario = ref(false)
    const desejaEncerrar = ref(false)

    // Refs para estado
    const carregando = ref(false)
    const carregandoProntuario = ref(false)
    const carregandoLaudo = ref(false)
    const consultasDisponiveis = ref([])
    const mensagemAlerta = ref('')
    const modoDebug = ref(true)

    // Refs para dados carregados do servidor
    const prontuarioCarregado = ref(false)
    const idProntuario = ref(null)
    const conteudoProntuario = ref('')
    const laudosCarregados = ref([])

    // Computed
    const examesSelecionados = computed(() => {
      return consultasDisponiveis.value.filter(consulta => 
        consulta.selecionado && consulta.disponivel
      )
    })

    const podeEncerrar = computed(() => {
      return examesSelecionados.value.length > 0
    })

    const temProntuarioValido = computed(() => {
      return conteudoProntuario.value && 
             conteudoProntuario.value.trim() !== '' && 
             conteudoProntuario.value !== '<p></p>' &&
             conteudoProntuario.value !== '<p><br></p>'
    })

    const examesComLaudoObrigatorio = computed(() => {
      return examesSelecionados.value.filter(consulta => 
        consulta.exigeLaudoClinico
      ).length
    })

    const laudosPreenchidos = computed(() => {
      return examesSelecionados.value.filter(consulta => 
        consulta.exigeLaudoClinico && consulta.laudoPreenchido
      ).length
    })

    const temAlertasBloqueantes = computed(() => {
      const temLaudosPendentes = examesSelecionados.value.some(
        consulta => consulta.exigeLaudoClinico && !consulta.laudoPreenchido
      )
      const temProntuarioObrigatorio = examesSelecionados.value.some(
        consulta => consulta.exigeProntuario
      )
      return temLaudosPendentes || (temProntuarioObrigatorio && !temProntuarioValido.value)
    })

    const temLaudosPreenchidos = computed(() => {
      return examesComLaudoObrigatorio.value === 0 || laudosPreenchidos.value === examesComLaudoObrigatorio.value
    })

    const alertas = computed(() => {
      const alertasList = []

      // Verificar laudos obrigatórios não preenchidos
      const laudosPendentes = examesSelecionados.value.filter(consulta => 
        consulta.exigeLaudoClinico && !consulta.laudoPreenchido
      )

      if (laudosPendentes.length > 0) {
        alertasList.push(`${laudosPendentes.length} exame(s) com laudo obrigatório não preenchido`)
      }

      // Verificar fichas ASO obrigatórias não preenchidas
      const fichasPendentes = examesSelecionados.value.filter(consulta => 
        consulta.exigeFichaAso && (!consulta.statusFormulario || !consulta.statusFormulario.existeAlgumFormulario)
      )

      if (fichasPendentes.length > 0) {
        alertasList.push(`${fichasPendentes.length} ficha(s) ASO obrigatória(s) não preenchida(s)`)
      }

      // Verificar prontuário obrigatório
      const examesComProntuarioObrigatorio = examesSelecionados.value.filter(
        consulta => consulta.exigeProntuario
      )

      if (examesComProntuarioObrigatorio.length > 0 && !temProntuarioValido.value) {
        alertasList.push('Prontuário obrigatório não preenchido')
      }

      return alertasList
    })

    // Métodos para buscar prontuário e laudos
    const buscarProntuarioPorIdExamesOS = async () => {
      if (prontuarioCarregado.value) return
      
      carregandoProntuario.value = true
      console.log('Buscando prontuário para idExamesOS:', props.mFicha.idExamesos, props.user)
      
      try {
        const { status, data } = await medicoService.medico.buscarProntuarioPorIdExamesOS(props.mFicha.idExamesos, props.user.id)
        console.log('Resposta do servidor (prontuário):', status, data);
        
        if (status === 200 && data) {
          prontuarioCarregado.value = true
          idProntuario.value = data.idProntuario
          conteudoProntuario.value = data.registro || ''
          
          console.log('Prontuário carregado:', {
            idProntuario: idProntuario.value,
            conteudo: conteudoProntuario.value,
            valido: temProntuarioValido.value
          })
          
          emit('prontuario-carregado', {
            conteudo: conteudoProntuario.value,
            idProntuario: idProntuario.value
          })
        } else {
          console.log('Nenhum prontuário encontrado ou dados inválidos')
          conteudoProntuario.value = ''
        }
      } catch (error) {
        console.error('Erro ao buscar prontuário:', error)
        mostrarAlerta('Erro ao carregar prontuário')
        conteudoProntuario.value = ''
      } finally {
        carregandoProntuario.value = false
      }
    }

    const buscarLaudoClinicoPorIdExamesOS = async (idExamesOS) => {
      if (!idExamesOS) {
        console.error('ID do exame não encontrado para buscar laudo')
        return
      }

      carregandoLaudo.value = true
      try {
        const response = await medicoService.medico.buscarLaudoClinicoPorIdExamesOS(idExamesOS)
        
        if (response.status === 200 && response.data) {
          const laudoData = response.data
          laudosCarregados.value.push(laudoData)
          
          // Atualizar a consulta correspondente com os dados do laudo
          const consultaIndex = consultasDisponiveis.value.findIndex(
            consulta => consulta.idexamesos === idExamesOS || consulta.idExamesOS === idExamesOS
          )
          
          if (consultaIndex !== -1) {
            const laudoPreenchido = !!laudoData.descricaoLaudo && laudoData.descricaoLaudo.trim() !== ''
            
            consultasDisponiveis.value[consultaIndex].laudoPreenchido = laudoPreenchido
            consultasDisponiveis.value[consultaIndex].laudo = laudoData.descricaoLaudo || ''
            consultasDisponiveis.value[consultaIndex].statusLaudo = laudoData.status || 'X'
            consultasDisponiveis.value[consultaIndex].idLaudo = laudoData.id
            
            console.log(`Laudo para exame ${consultasDisponiveis.value[consultaIndex].nomeExame}:`, {
              preenchido: laudoPreenchido,
              conteudo: laudoData.descricaoLaudo,
              idLaudo: laudoData.id
            })
            
            emit('laudo-carregado', {
              idExamesOS: idExamesOS,
              laudo: laudoData
            })
          }
        } else {
          console.log('Nenhum laudo encontrado para ID:', idExamesOS)
        }
      } catch (error) {
        console.error('Erro ao buscar laudo:', error)
      } finally {
        carregandoLaudo.value = false
      }
    }

    const buscarLaudosParaConsultas = async () => {
      const consultasComLaudo = consultasDisponiveis.value.filter(
        consulta => consulta.exigeLaudoClinico
      )
      
      console.log(`Buscando laudos para ${consultasComLaudo.length} exames que exigem laudo`)
      
      for (const consulta of consultasComLaudo) {
        const idExamesOS = consulta.idexamesos || consulta.idExamesOS
        if (idExamesOS) {
          await buscarLaudoClinicoPorIdExamesOS(idExamesOS)
        }
      }
    }

    const verificarDisponibilidadeExame = (consulta) => {
      try {
        console.log('=== VERIFICANDO EXAME ===')
        console.log('Exame:', consulta.nomeExame)
        console.log('ID Exame:', consulta.idExame)
        console.log('ID Médico consulta:', consulta.idMedico)
        console.log('ID Médico usuário:', props.user.id)
        console.log('Status:', consulta.status)
        console.log('Unidade consulta:', consulta.idUnidade)
        console.log('Unidade usuário:', props.user.idUnidade)

        // 1. Verificar se o médico é responsável
        if (consulta.idMedico != null && consulta.idMedico != props.user.id) {
          console.log('❌ Médico não responsável')
          return {
            disponivel: false,
            motivo: 'Médico não responsável',
            debugInfo: 'Médico difere'
          }
        }

        console.log('✅ Exame disponível para seleção (sem filtro por serviço)')
        return {
          disponivel: true,
          motivo: 'Sem filtro por serviço',
          debugInfo: 'Disponível'
        }

      } catch (error) {
        console.error('Erro na verificação do exame:', error)
        // Em caso de erro, permite seleção para não bloquear o usuário
        return {
          disponivel: true,
          motivo: 'Erro - permitindo como fallback',
          debugInfo: 'Erro'
        }
      }
    }

    const processarStatusConsulta = (consulta) => {
      const disponibilidade = verificarDisponibilidadeExame(consulta)
      
      consulta.disponivel = disponibilidade.disponivel
      consulta.debugInfo = modoDebug.value ? disponibilidade.debugInfo : null
      consulta.laudoPreenchido = false
      consulta.laudo = ''
      
      // Define seleção padrão baseada na disponibilidade
      consulta.selecionado = disponibilidade.disponivel

      // Aplica estilos visuais
      if (!disponibilidade.disponivel) {
        consulta.colorText = "text-grey-lighten-1"
        consulta.cor = "grey-lighten-1"
      } else {
        consulta.colorText = ""
        consulta.cor = ""
      }

      console.log(`Exame: ${consulta.nomeExame}, Disponível: ${disponibilidade.disponivel}, Motivo: ${disponibilidade.motivo}`)
    }

    const carregarConsultasDisponiveis = async () => {
      console.log('=== CARREGANDO CONSULTAS DISPONÍVEIS ===')
      console.log('Consultas recebidas:', props.consultasPaciente)
      console.log('Dados do usuário:', props.user)

      consultasDisponiveis.value = props.consultasPaciente.map(consulta => {
        const consultaProcessada = {
          ...consulta,
          selecionado: false,
          disponivel: false,
          files: consulta.files || [],
          colorText: "",
          cor: "",
          debugInfo: null,
          laudoPreenchido: false,
          laudo: ''
        }
        
        processarStatusConsulta(consultaProcessada)
        return consultaProcessada
      })

      console.log('Consultas processadas:', consultasDisponiveis.value)

      // Buscar prontuário e laudos após processar as consultas
      await buscarProntuarioPorIdExamesOS()
      await buscarLaudosParaConsultas()
    }

    const toggleExame = (consulta) => {
      if (consulta.disponivel) {
        consulta.selecionado = !consulta.selecionado
        console.log(`Exame ${consulta.nomeExame} ${consulta.selecionado ? 'selecionado' : 'desselecionado'}`)
      } else {
        console.log(`Exame ${consulta.nomeExame} não pode ser selecionado - Disponível: ${consulta.disponivel}`)
      }
    }

    const getCorExame = (consulta) => {
      if (!consulta.disponivel) return 'grey'
      if (consulta.exigeLaudoClinico && !consulta.laudoPreenchido) return 'warning'
      if (consulta.exigeProntuario && !temProntuarioValido.value) return 'warning'
      if (consulta.exigeFichaAso && !consulta.statusFormulario?.existeAlgumFormulario) return 'warning'
      return 'primary'
    }

    const fechar = () => {
      emit('fechar')
    }

    const mostrarAlerta = (mensagem) => {
      mensagemAlerta.value = mensagem
      dialogAlerta.value = true
    }

    const verificarExamesComStatusAberto = () => {
      if (!podeEncerrar.value) {
        mostrarAlerta('Selecione pelo menos um exame para encerrar o atendimento.')
        return
      }

      // Verificar laudos obrigatórios
      const temLaudosPendentes = examesSelecionados.value.some(
        consulta => consulta.exigeLaudoClinico === 1 && !consulta.laudoPreenchido
      )

      if (temLaudosPendentes) {
        dialogLaudo.value = true
        return
      }

      // Verificar prontuário obrigatório
      const temProntuarioObrigatorio = examesSelecionados.value.some(
        consulta => consulta.exigeProntuario === 1
      )

      if (temProntuarioObrigatorio) {
        if (!temProntuarioValido.value) {
          dialogProntuario.value = true
          return
        }
      }

      // Verificar se há exames complementares solicitados
      const temSolicitacoes = examesSelecionados.value.some(consulta => 
        consulta.solicitacoes && consulta.solicitacoes.length > 0
      )

      if (!temSolicitacoes) {
        confirmarEncerramentoForcado()
        return
      }

      // Se passou por todas as verificações, mostrar confirmação final
      desejaEncerrar.value = true
    }

    const gerarVoucher = (tipoSolicitacao, exames, armazenar = true, observacao = "") => {
      posAtendimentoService.gerarVoucher({
        exames: exames,
        idEmpresa: props.user.idEmpresa,
        idUnidadeNegocio: props.user.idUnidade,
        tipoSolicitacao: tipoSolicitacao,
        idPaciente: props.mFicha.idPaciente,
        idPrestadorSolicitante: props.user.idPrestador,
        idUsuario: props.user.id,
        armazenar: armazenar,
        obs: observacao
      }).then(r => {
        console.log('Voucher gerado:', r.data);
      }).catch(error => {
        console.error('Erro ao gerar voucher:', error);
      });
    }

    const encerrarVoucher = (armazenar) => {
      if (props.user.voucher === 'POSAUTOMATICO') {

        const solicitacoesClinicas = examesSelecionados.value.filter(e => e.tipo === 'CLINICA');
        const solicitacoesLab = examesSelecionados.value.filter(e => e.tipo === 'LABORATORIO');

        const examesClinicos = solicitacoesClinicas.map(item => ({ id: item.idExame }));
        const examesLab = solicitacoesLab.map(item => ({ id: item.idExame }));

        if (examesClinicos.length > 0) {
          console.log('Gerando voucher para exames clínicos:', examesClinicos);
          gerarVoucher('CLINICA', examesClinicos, armazenar, '');
        }

        if (examesLab.length > 0) {
          console.log('Gerando voucher para exames de laboratório:', examesLab);
          gerarVoucher('LABORATORIO', examesLab, armazenar, '');
        }
      }
    }

    const confirmarEncerramentoForcado = () => {
      dialogExameComplementar.value = false
      desejaEncerrar.value = true
    }

    const confirmarEncerramento = async () => {
      desejaEncerrar.value = false
      carregando.value = true

      // Gerar voucher se necessário
      encerrarVoucher(false)

      try {
        // Preparar os IDs dos exames para a função finalizarApi
        const examesIds = examesSelecionados.value.map(consulta => consulta.idexamesos)
        
        // Chamar a função finalizarApi de forma assíncrona (fire-and-forget)
        medicoService.medico.finalizarApi(props.user.id, examesIds).then(r => {
          console.log('Retorno (assíncrono) finalizarApi', r.data)
        }).catch(err => {
          console.log('Erro (assíncrono) finalizarApi', err)
        })

        // Preparar dados para a chamada principal de encerramento
        const consultasSelecionadas = examesSelecionados.value.map(consulta => ({
          idConsulta: consulta.idConsulta,
          idexamesos: consulta.idexamesos,
          idExame: consulta.idExame,
          nomeExame: consulta.nomeExame,
          status: true // Replicando o `exame.status == true` do código antigo
        }))

        const dataChegada = new Date(props.mFicha.dataChegada || Date.now()).toISOString()
        const nomeConsultorio = props.user.nomeConsultorioCompleto || 'Consultório'
        const isMedico = !props.user.tecnico
        const idUnidadeServico = examesSelecionados.value[0]?.idUnidade || props.user.idUnidade

        // Chamar a função principal de encerramento e aguardar sua resposta
        const resultadoEncerramento = await medicoService.medico.encerrarAtendimento(
          props.user.id,
          idUnidadeServico,
          props.mFicha.idAtendimento,
          props.mFicha.idConsulta,
          consultasSelecionadas,
          dataChegada,
          nomeConsultorio,
          isMedico,
          props.mFicha.idExamesos,
          props.mFicha.idOrdemServico
        )

        console.log("rsp encerrarAtendimento:", resultadoEncerramento.data)

        if (resultadoEncerramento.data) {
          // Lógica de sucesso do encerramento
          emit('atendimento-encerrado', {
            sucesso: true,
            dados: resultadoEncerramento.data,
            examesEncerrados: examesSelecionados.value
          })
          fechar()
        } else {
          // Se a resposta for `false` ou vazia, lança o erro
          throw new Error('Falha ao encerrar atendimento no sistema')
        }

      } catch (error) {
        console.error('Erro ao encerrar atendimento:', error)
        mostrarAlerta('Erro ao encerrar atendimento. Tente novamente.')
        emit('atendimento-encerrado', {
          sucesso: false,
          erro: error.message
        })
      } finally {
        carregando.value = false
      }
    }

    // Watchers
    watch(() => props.consultasPaciente, () => {
      carregarConsultasDisponiveis()
    })

    watch(() => props.user, () => {
      carregarConsultasDisponiveis()
    }, { deep: true })

    onMounted(() => {
      carregarConsultasDisponiveis()
    })

    return {
      // Diálogos
      dialogAlerta,
      dialogExameComplementar,
      erroProntuarioVazio,
      dialogLaudo,
      dialogProntuario,
      desejaEncerrar,
      
      // Estado
      carregando,
      carregandoProntuario,
      carregandoLaudo,
      consultasDisponiveis,
      mensagemAlerta,
      modoDebug,
      conteudoProntuario,

      // Computed
      examesSelecionados,
      podeEncerrar,
      temProntuarioValido,
      temLaudosPreenchidos,
      temAlertasBloqueantes,
      laudosPreenchidos,
      examesComLaudoObrigatorio,
      alertas,
      
      // Métodos
      toggleExame,
      getCorExame,
      fechar,
      verificarExamesComStatusAberto,
      confirmarEncerramentoForcado,
      confirmarEncerramento,
      mostrarAlerta,
      gerarVoucher,
      encerrarVoucher
    }
  }
}
</script>

<style scoped>
.encerramento-card {
  border: 2px solid rgba(var(--v-theme-primary), 0.1);
}

.resumo-card, .exames-card, .status-card {
  border: 1px solid rgba(var(--v-theme-primary), 0.1);
}

.exames-list {
  max-height: 400px;
  overflow-y: auto;
}

.exame-item {
  transition: all 0.3s ease;
  border: 1px solid rgba(0, 0, 0, 0.1);
}

.exame-selecionado {
  background-color: rgba(25, 118, 210, 0.08);
  border-left: 4px solid rgb(var(--v-theme-primary));
  border-color: rgba(25, 118, 210, 0.3);
}

.exame-obrigatorio {
  border-right: 3px solid rgb(var(--v-theme-warning));
}

.exame-invalido {
  background-color: rgba(0, 0, 0, 0.04);
  opacity: 0.6;
}

.exame-item:hover {
  background-color: rgba(0, 0, 0, 0.04);
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.status-chip {
  font-size: 0.65rem;
  height: 20px;
}

.action-btn {
  min-width: 120px;
}

.text-primary {
  color: rgb(var(--v-theme-primary));
}

/* Scrollbar personalizada */
.exames-list::-webkit-scrollbar {
  width: 6px;
}

.exames-list::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.exames-list::-webkit-scrollbar-thumb {
  background: rgba(var(--v-theme-primary), 0.3);
  border-radius: 3px;
}

.exames-list::-webkit-scrollbar-thumb:hover {
  background: rgba(var(--v-theme-primary), 0.5);
}

/* Responsividade */
@media (max-width: 768px) {
  .exames-list {
    max-height: 300px;
  }
  
  .status-chip {
    font-size: 0.6rem;
    height: 18px;
  }
}
</style>
